const activeModals = new Set();
import '@/scripts/UI/modal.css';
import { createElement, icons } from 'lucide';
import { currLang } from '@/scripts/translation.js';
/**
 * createModal
 *
 * @param {Object} options
 * @param {string|HTMLElement|() => (string|HTMLElement)} options.content
 *   Static string/element, or a function that returns one — called fresh
 *   every time open() runs, so it's the easy way to inject dynamic content.
 * @param {string}   [options.title]        Optional header text.
 * @param {boolean}  [options.closable=true] If false: no close button,
 *   overlay click does nothing, Escape does nothing. The modal can then
 *   only be closed programmatically via api.close().
 * @param {string}   [options.size='md']    'sm' | 'md' | 'lg' | 'full'
 * @param {Function} [options.onOpen]
 * @param {Function} [options.onClose]
 * @returns {{open,close,toggle,isOpen,setContent,destroy}}
 */
export function createModal({
  content,
  title,
  closable = true,
  size = 'md',
  onOpen,
  onClose,
} = {}) {
  let isOpen = false;
  let lastFocused = null;

  // --- build DOM once -------------------------------------------------
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.setAttribute('aria-hidden', 'true');

  const panel = document.createElement('div');
  panel.className = `modal-panel modal-panel--${size}`;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  if (title) panel.setAttribute('aria-label', title);

  const header = document.createElement('div');
  header.className = 'modal-panel__header';

  const titleEl = document.createElement('h3');
  titleEl.className = 'modal-panel__title';
  //   titleEl.textContent = title ?? '';
  header.appendChild(titleEl);

  let closeBtn = null;
  if (closable) {
    closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'modal-panel__close';
    closeBtn.setAttribute('aria-label', currLang['UI.Close']);
    closeBtn.appendChild(createElement(icons.X, { width: 16, height: 16 }));
    closeBtn.addEventListener('click', () => close());
    header.appendChild(closeBtn);
  }

  const body = document.createElement('div');
  body.className = 'modal-panel__body';

  panel.appendChild(header);
  panel.appendChild(body);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  // --- content injection ------------------------------------------------
  function renderContent() {
    const resolvedTitle = typeof title === 'function' ? title() : title;
    titleEl.textContent = resolvedTitle ?? '';
    if (resolvedTitle) panel.setAttribute('aria-label', resolvedTitle);

    const resolved = typeof content === 'function' ? content() : content;

    body.innerHTML = '';
    if (resolved instanceof HTMLElement) {
      body.appendChild(resolved);
    } else if (resolved != null) {
      body.innerHTML = String(resolved);
    }
  }

  function setContent(next) {
    content = next;
    if (isOpen) renderContent();
  }

  // --- open / close -------------------------------------------------
  const close = () => {
    if (!isOpen) return;

    isOpen = false;
    overlay.classList.remove('modal-overlay--open');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.zIndex = '';

    lastFocused?.focus?.();
    activeModals.delete(api);

    // only remove our own lock class, never your app's 'modal-open'
    if (activeModals.size === 0) {
      document.body.classList.remove('modal-scroll-lock');
    }

    onClose?.();
  };

  const open = () => {
    if (isOpen) return;

    renderContent();

    lastFocused = document.activeElement;
    isOpen = true;

    // stack on top of whatever's already open
    overlay.style.zIndex = String(9999 + activeModals.size * 10);

    overlay.classList.add('modal-overlay--open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-scroll-lock');

    (closeBtn ?? panel).focus?.();
    activeModals.add(api);
    onOpen?.();
  };

  const toggle = () => (isOpen ? close() : open());

  // click on the dimmed backdrop (not the panel) — only when closable
  overlay.addEventListener('click', (e) => {
    if (!closable) return;
    if (e.target === overlay) close();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !isOpen || !closable) return;
    // only the topmost modal should respond to Escape
    const topmost = [...activeModals].pop();
    if (topmost !== api) return;
    close();
  });

  // basic focus trap while open
  panel.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || !isOpen) return;

    const focusables = panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  function destroy() {
    close();
    overlay.remove();
    activeModals.delete(api);
  }

  const api = {
    open,
    close,
    toggle,
    isOpen: () => isOpen,
    setContent,
    destroy,
    setClosable: (next) => {
      closable = next;
      if (closeBtn) closeBtn.style.display = closable ? '' : 'none';
    },
    elements: { overlay, panel, body },
  };

  return api;
}

//   // 1. Basic, closable modal — content is a plain string
//   const basicModal = createModal({
//     title: 'About this block',
//     closable: true,
//     content: `
//       <p>This is the <strong>Run</strong> block. It marks the entry point
//       of your program — drag other blocks underneath it to build your
//       script.</p>
//       <p>Click outside, press <kbd>Esc</kbd>, or hit the close button to
//       dismiss this.</p>
//     `,
//   });
//   document.getElementById('btn-basic').addEventListener('click', basicModal.open);

//   // 2. Dynamic modal — content is a function, so it can pull fresh data
//   // (or build a live DOM node with real event listeners) every time open() runs
//   let clickCount = 0;
//   const dynamicModal = createModal({
//     title: 'Block inspector',
//     closable: true,
//     content: () => {
//       clickCount++;
//       const wrapper = document.createElement('div');
//       wrapper.innerHTML = `<p>Opened <strong>${clickCount}</strong> time(s).</p>`;

//       const field = document.createElement('div');
//       field.className = 'field';
//       field.innerHTML = `
//         <label>Block name</label>
//         <input type="text" placeholder="e.g. Run" />
//       `;
//       wrapper.appendChild(field);

//       const actions = document.createElement('div');
//       actions.className = 'modal-actions';
//       actions.innerHTML = `
//         <button class="btn-secondary" type="button">Cancel</button>
//         <button class="btn-primary" type="button">Save</button>
//       `;
//       actions.children[0].addEventListener('click', () => dynamicModal.close());
//       actions.children[1].addEventListener('click', () => {
//         alert('Saved: ' + field.querySelector('input').value);
//         dynamicModal.close();
//       });
//       wrapper.appendChild(actions);

//       return wrapper;
//     },
//   });
//   document.getElementById('btn-dynamic').addEventListener('click', dynamicModal.open);

//   // 3. Persistent modal — closable: false means no close button,
//   // overlay-click does nothing, Escape does nothing.
//   // The only way out is api.close() from your own logic.
//   const persistentModal = createModal({
//     title: 'Saving your project…',
//     closable: false,
//     content: `<p>Please wait while we save. This will close automatically.</p>`,
//   });
//   document.getElementById('btn-persistent').addEventListener('click', () => {
//     persistentModal.open();
//     setTimeout(() => persistentModal.close(), 2500);
//   });
